import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notifications } from '@mantine/notifications'
import { syncMaps } from '@/api/mapSyncApi/sdk.gen'
import type { SyncMapsRequest } from '@/api/mapSyncApi/types.gen'
import { createVehicleProxyClient } from '@/api-proxy/useProxiedClient'
import { errorMessage } from '@/utils/other'

export type MapSyncStatus = 'pending' | 'ok' | 'inProgress' | 'error'

export type MapSyncOutcome = {
  status: MapSyncStatus
  detail?: string
}

export type MapSyncOutcomes = Record<string, MapSyncOutcome>

export const MAP_SYNC_STATUS_COLOR: Record<MapSyncStatus, string> = {
  pending: 'blue',
  ok: 'green',
  inProgress: 'yellow',
  error: 'red',
}

const readDetail = (payload: unknown): string | undefined =>
  payload && typeof payload === 'object' && 'detail' in payload && typeof payload.detail === 'string'
    ? payload.detail
    : undefined

const requestVehicleMapSync = async (vehicleId: string, body: SyncMapsRequest): Promise<MapSyncOutcome> => {
  try {
    const client = createVehicleProxyClient(vehicleId, 'mapSyncer')
    const { data, error, response } = await syncMaps({ client, body })

    if (data?.status === 'ok') {
      return { status: 'ok' }
    }

    if (response.status === 409) {
      return { status: 'inProgress', detail: readDetail(error) }
    }

    return { status: 'error', detail: readDetail(error) ?? `HTTP ${response.status}` }
  } catch (caught) {
    return { status: 'error', detail: errorMessage(caught, '') || undefined }
  }
}

export const useVehicleMapSync = () => {
  const { t } = useTranslation('vehicles')
  const [outcomes, setOutcomes] = useState<MapSyncOutcomes>({})
  const [isSyncing, setIsSyncing] = useState(false)

  const syncVehicles = useCallback(
    async (vehicleIds: string[], body: SyncMapsRequest) => {
      if (vehicleIds.length === 0 || isSyncing) return

      setOutcomes(Object.fromEntries(vehicleIds.map(vehicleId => [vehicleId, { status: 'pending' as MapSyncStatus }])))
      setIsSyncing(true)

      await Promise.all(
        vehicleIds.map(async vehicleId => {
          const outcome = await requestVehicleMapSync(vehicleId, body)

          setOutcomes(previous => ({ ...previous, [vehicleId]: outcome }))

          notifications.show({
            title: t('syncMaps'),
            message:
              outcome.status === 'ok'
                ? t('mapSyncOk', { vehicleId })
                : outcome.status === 'inProgress'
                  ? t('mapSyncInProgress', { vehicleId })
                  : t('mapSyncFailed', { vehicleId, detail: outcome.detail ?? t('mapSyncUnknownError') }),
            color: MAP_SYNC_STATUS_COLOR[outcome.status],
          })
        })
      )

      setIsSyncing(false)
    },
    [isSyncing, t]
  )

  return { outcomes, isSyncing, syncVehicles }
}
